import type { UpdateRequestUnion } from "@nlc/shared-types";

import {
  BriefCreatorTypeEnum,
  RequestTypeEnum,
  updateRequestUnionType,
} from "@nlc/shared-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { useCapturePosthogEvent } from "@/hooks/posthog/useCapturePosthogEvent";
import { requestListKeys } from "@/hooks/queryKeys";
import authenticatedApi from "@/lib/api/client";

import type { CreateWebsiteImplementationRequestFormData } from "../components/useCreateWebsiteImplementationRequestForm";

import { getValidAttachmentIds } from "../../shared/utils";

type UseSaveDraftMutationProps = {
  draftId: number | undefined;
};

export function useSaveDraftMutation({ draftId }: UseSaveDraftMutationProps) {
  const capture = useCapturePosthogEvent();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      formData: CreateWebsiteImplementationRequestFormData,
    ) => {
      if (!draftId) throw new Error("No request ID found");

      const body: UpdateRequestUnion = {
        attachmentIds: [
          ...getValidAttachmentIds(formData.assets),
          ...getValidAttachmentIds(formData.inspirations),
        ],
        brandId: formData.brandId ?? undefined,
        briefCreatorType: BriefCreatorTypeEnum.SINGLE,
        collaboratorIds: formData.collaboratorIds,
        complexityId: formData.designComplexityId,
        description: formData.requestDescriptionHTML,
        designTypeId: formData.designTypeId,
        name: formData.requestName,
        preferredDesignerIds: formData.preferredDesignerIds,
        requestType: RequestTypeEnum.WEBSITE_IMPLEMENTATION,
        textForDesign: formData.requestDescriptionHTML,
      };
      const parsed = updateRequestUnionType.parse(body);

      return authenticatedApi
        .url(`/requests/${draftId}`)
        .patch(parsed)
        .json<{ message: string; request_data: unknown }>();
    },
    onError: (error) => {
      console.error("Failed to save draft:", error);

      toast.add(
        {
          description: error.message,
          title: "Error saving draft",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: () => {
      capture("brief_draft_saved", {
        draft_id: draftId!,
      });

      queryClient.invalidateQueries({ queryKey: requestListKeys.all });

      toast.add(
        {
          title: "Draft saved successfully",
          type: "success",
        },
        {
          timeout: 5000,
        },
      );
    },
  });
}
